import {
  Box,
  Typography,
  Button,
  Container,
  useTheme,
  Paper,
  Avatar,
  IconButton,
} from "@mui/material";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { LinkedIn, GitHub } from "@mui/icons-material";
import DataObjectIcon from "@mui/icons-material/DataObject";

const HomeAbout = () => {
  const theme = useTheme();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
  };

  return (
    <Box id="home">
      {/* Header Section */}
      <Box
        id="home"
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              theme.palette.mode === "dark"
                ? "radial-gradient(circle at 20% 50%, rgba(56, 189, 248, 0.1) 0%, transparent 50%)"
                : "radial-gradient(circle at 20% 50%, rgba(37, 99, 235, 0.05) 0%, transparent 50%)",
            zIndex: -1,
          },
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{ textAlign: "center" }}
          >
            <motion.div variants={itemVariants}>
              <Typography variant="h4" component="h4" gutterBottom>
                Greetings!!!
              </Typography>
            </motion.div>
            <motion.div variants={itemVariants}>
              <Typography variant="h1" component="h1" gutterBottom>
                I'm{" "}
                <span style={{ fontWeight: 800 }}>Sohanur Rahman Shimul</span>
              </Typography>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Typography
                variant="h5"
                component="h2"
                gutterBottom
                sx={{
                  mb: 4,
                  fontFamily: '"Roboto Mono", monospace',
                  color: theme.palette.text.secondary,
                }}
              >
                Tech Enthusiast
              </Typography>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Box sx={{ display: "flex", gap: 3, justifyContent: "center" }}>
                <Button
                  component={Link}
                  to="#projects"
                  variant="contained"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    boxShadow: theme.shadows[4],
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: theme.shadows[8],
                    },
                  }}
                >
                  View My Work
                </Button>
                <Button
                  component={Link}
                  to="#contact"
                  variant="outlined"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    borderWidth: "2px",
                    "&:hover": {
                      borderWidth: "2px",
                      transform: "translateY(-3px)",
                    },
                  }}
                >
                  Contact Me
                </Button>
              </Box>
            </motion.div>

            <motion.div variants={itemVariants} style={{ marginTop: "4rem" }}>
              <Box
                component={motion.div}
                animate={{
                  y: [0, -15, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  color: theme.palette.text.secondary,
                }}
              >
                <Typography variant="caption">Scroll Down</Typography>
              </Box>
            </motion.div>
          </motion.div>
        </Container>
      </Box>

      {/* About Section */}
      <Box id="about" sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Typography variant="h4" gutterBottom align="center">
              About Me
            </Typography>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 4,
                alignItems: { md: "center" },
              }}
            >
              <Avatar
                src="media/images/Professional Portrait in Soft Office Light.png"
                sx={{
                  width: 200,
                  height: 200,
                  mx: "auto",
                  mb: { xs: 2, md: 0 },
                  flexShrink: 0,
                }}
              />
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  style={{ marginBottom: "25px" }}
                >
                  Hi, I'm Shimul
                </Typography>
                <Typography style={{ marginBottom: "25px" }}>
                  Currently pursuing B.Sc. in Computer Science and Engineering
                  at BRAC University, with a strong passion for coding,
                  continuous learning, and emerging technologies.
                </Typography>
                <Typography style={{ marginBottom: "25px" }}>
                  Over the past year, I've solved 150+ LeetCode problems using
                  Java and Python, honing my algorithmic thinking and
                  problem-solving skills. I am also proficient in full-stack web
                  development using the MERN stack and have a growing enthusiasm
                  for machine learning.
                </Typography>

                <Typography style={{ marginBottom: "25px" }}>
                  Alongside software development, I've built several
                  hardware-based projects including an Advanced Logic Design
                  Circuit, a One-Way Traffic Light System, and an IC Tester
                  using Raspberry Pi and Arduino Uno — gaining hands-on
                  experience with real-world embedded systems and digital
                  electronics.
                </Typography>

                <Typography style={{ marginBottom: "25px" }}>
                  Dedicated to build efficient, scalable applications and eager
                  to contribute into real life projects, I thrive in dynamic
                  environments where I can continuously learn, adapt, and grow.
                  Seeking opportunities to apply my skills in software
                  development field.
                </Typography>

                {/* Social Media Icons */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mt: 4,
                    justifyContent: { xs: "center", md: "flex-start" },
                  }}
                >
                  <IconButton
                    component="a"
                    href="https://www.linkedin.com/in/shimul47"
                    target="_blank"
                    rel="noopener"
                    sx={{
                      color: "primary.main",
                      "&:hover": {
                        transform: "scale(1.2)",
                        transition: "transform 0.2s",
                      },
                    }}
                    aria-label="LinkedIn"
                  >
                    <LinkedIn sx={{ fontSize: 32 }} />
                  </IconButton>

                  <IconButton
                    component="a"
                    href="https://github.com/shimul47"
                    target="_blank"
                    rel="noopener"
                    sx={{
                      color: "primary.main",
                      "&:hover": {
                        transform: "scale(1.2)",
                        transition: "transform 0.2s",
                      },
                    }}
                    aria-label="GitHub"
                  >
                    <GitHub sx={{ fontSize: 32 }} />
                  </IconButton>

                  <IconButton
                    component="a"
                    href="https://leetcode.com/u/sohanurrahmanshimul"
                    target="_blank"
                    rel="noopener"
                    sx={{
                      color: "primary.main",
                      "&:hover": {
                        transform: "scale(1.2)",
                        transition: "transform 0.2s",
                      },
                    }}
                    aria-label="LeetCode"
                  >
                    <DataObjectIcon sx={{ fontSize: 32 }} />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default HomeAbout;
